/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Text,
    DropdownCheckboxes,
    Panel,
    RangeSelectionPanel,
} from "vr-react-components"
import BarWidgetComparePanel from "./BarWidgetComparePanel";
import BarWidgetCluePanel from "./BarWidgetCluePanel";
import { setChecked, setRange } from "../../store/slice/centerGraphSlice";

function BarWidgetRadialPanels({
    width,
    stateName,
    height,
    fontColor = "#c6c6c6",
    ...otherAttributes
}) {
    const data = useSelector((state) => state[stateName].data);
    const currentData = useSelector((state) => state[stateName].currentData);
    const checked = useSelector((state) => state[stateName].currentData.checked);
    const device = useSelector((state) => state.private.device);
    const isVROrMobile = device.isMobile || device.areControllersConnected;
    const manipulation = useSelector((state) => state[stateName].isManipulable.settings);
    const graphScale = useSelector((state) =>
        (device.inVRMode && manipulation.enabled.VR.includes("zoom")) ||
            (!device.inVRMode && manipulation.enabled.desktop.includes("zoom"))
            ? state[stateName].position.scale
            : 1,
    );

    const dispatch = useDispatch();

    if (!data || !currentData) return;

    const handleSetChecked = (id) => {
        if (!id) {
            if (checked.includes(false))
                dispatch(setChecked(data.zLabels.map(() => true)))
            else dispatch(setChecked(data.zLabels.map(() => false)))
        } else dispatch(setChecked(data.zLabels.map((e, i) => (i === id - 1) ? !checked[i] : checked[i])))
    }

    const circleRadius = (width / 2 > height ? width / 2 : height) * 1.5;
    const panelWidth = 2;
    const panelHeight = 1;

    return (
        <a-entity class="BarWidgetRadialPanels" {...otherAttributes}>
            <a-entity position={`0 ${panelHeight / 2} 0`}>
                <Panel
                    id="top"
                    width={panelWidth}
                    height={panelHeight}
                    panel
                    aframeClasses={device.canModify ? "collidable" : ""}
                    position={`0 ${(circleRadius - panelHeight / 2) * graphScale} 0`}
                    rotation={`${isVROrMobile ? 45 : 0} 0 0`}
                >
                    <a-entity
                        position={`${-panelWidth / 2 + panelWidth * 0.05} ${panelHeight / 2 - panelHeight / 10} 0.01`}
                    >
                        <Text
                            text={`Select ${data.axis.z}`}
                            width={panelWidth * 0.9}
                            align="left"
                            size="p3"
                            baseline="top"
                            color={fontColor}
                        />
                        <Text
                            value={currentData.axis.z}
                            width={panelWidth}
                            align="left"
                            size="p5"
                            color={fontColor}
                            position={`0 ${-panelHeight / 5 - (panelHeight / 10) * 2} 0`}
                        />
                        <DropdownCheckboxes
                            id={data.zLabels[0]}
                            width={panelWidth * 0.9}
                            height={panelHeight / 5}
                            aframeClasses={device.canModify ? "collidable" : ""}
                            position={`${(panelWidth * 0.9) / 2} ${-panelHeight / 5 - (panelHeight / 10) * 4} 0`}
                            text={data.axis.z}
                            choices={data.zLabels}
                            textSize="p5"
                            checked={checked}
                            setChecked={(id) => handleSetChecked(id)}
                        />
                    </a-entity>
                </Panel>
                <BarWidgetCluePanel
                    position={`${(circleRadius * 0.7071 + panelWidth / 4) * graphScale} ${(circleRadius * 0.7071 - panelHeight / 2) * graphScale
                        } 0`}
                    rotation={`${isVROrMobile ? 22.5 : 0} ${isVROrMobile ? -45 : 0} 0`}
                    axe={data.axis.y}
                    width={panelWidth}
                    height={panelHeight}
                    stateName={stateName}
                />
                <Panel
                    id="left45"
                    width={panelWidth}
                    height={panelHeight}
                    panel
                    aframeClasses={device.canModify ? "collidable" : ""}
                    position={`${(circleRadius * -0.7071 - panelWidth / 4) * graphScale
                        } ${(circleRadius * 0.7071 - panelHeight / 2) * graphScale} 0`}
                    rotation={`${isVROrMobile ? 22.5 : 0} ${isVROrMobile ? 45 : 0} 0`}
                >
                    <RangeSelectionPanel
                        title={data.compare.rangeTitle}
                        width={panelWidth}
                        height={panelHeight}
                        textSize={20}
                        aframeClasses={device.canModify ? "collidable" : ""}
                        from={{
                            text: "From",
                            current: currentData.xLabels[0],
                            choices: data.xLabels.slice(
                                0,
                                data.xLabels.indexOf(
                                    currentData.xLabels[currentData.xLabels.length - 1],
                                ) + 1,
                            ),
                            setCurrent: (from) => {
                                dispatch(setRange({ to : currentData.range.to, from }));
                            },
                        }}
                        to={{
                            text: "To",
                            current: currentData.xLabels[currentData.xLabels.length - 1],
                            choices: data.xLabels.slice(
                                data.xLabels.indexOf(currentData.xLabels[0]),
                            ),
                            setCurrent: (to) => {
                                dispatch(setRange({ to, from : currentData.range.from }));
                            }
                        }}
                    />
                </Panel>
                <BarWidgetComparePanel
                    width={panelWidth}
                    height={panelHeight}
                    stateName={stateName}
                    device={device}
                    manipulation={manipulation}
                    position={`${(circleRadius + width / 4) * graphScale} 0 0`}
                />
                <Panel
                    id="left"
                    width={panelWidth}
                    height={panelHeight}
                    panel
                    aframeClasses={device.canModify ? "collidable" : ""}
                    position={`${(-circleRadius - panelWidth / 4) * graphScale} 0 0`}
                    rotation={`0 ${isVROrMobile ? 45 : 0} 0`}
                >
                    <a-entity
                        position={`${-panelWidth / 2 + panelWidth / 20} ${panelHeight / 2 - panelHeight / 10
                            } 0.01`}
                    >
                        <Text
                            text="Information"
                            width={panelWidth * 0.9}
                            align="left"
                            baseline="top"
                            color={fontColor}
                            size="p3"
                        />
                        <Text
                            text={`Title: ${data.title}`}
                            position={`0 ${-panelHeight / 5 - panelHeight / 10} 0`}
                            width={panelWidth * 0.9}
                            size="p5"
                            color={fontColor}
                            align="left"
                        />
                        <Text
                            text={`Axis X: ${data.axis.x}`}
                            position={`0 ${-panelHeight / 5 - (panelHeight / 10) * 3} 0`}
                            width={panelWidth * 0.9}
                            size="p5"
                            color={fontColor}
                            align="left"
                        />
                        <Text
                            text={`Axis Y: ${data.axis.y}`}
                            position={`0 ${-panelHeight / 5 - (panelHeight / 10) * 4} 0`}
                            width={panelWidth * 0.9}
                            size="p5"
                            color={fontColor}
                            align="left"
                        />
                        <Text
                            text={`Axis Z: ${data.axis.z}`}
                            position={`0 ${-panelHeight / 5 - (panelHeight / 10) * 5} 0`}
                            width={panelWidth * 0.9}
                            size="p5"
                            color={fontColor}
                            align="left"
                        />
                    </a-entity>
                </Panel>
            </a-entity>
        </a-entity>
    );
}

// requiring dataPoints
BarWidgetRadialPanels.propTypes = {};

export default BarWidgetRadialPanels;
